import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";
import React, { useState } from "react";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import google from "../../../src/images/login/google.png";
import carleft from "../../../src/images/login/login.png";
import logo from "../../../src/images/signup/logo.png";
import Loading from '../../loading';
import { login } from "../../redux/authSlice";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false); 
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password }
      );
      const { token, user } = response.data;
  
      if (!user) throw new Error("User not found in response");
      if (user.blocked) {
        alert("User is blocked. Please contact support.");
        return;
      }
  
      dispatch(login({ token, user, role: user.role }));
  
      const expirationTime = rememberMe
        ? Date.now() + 7 * 24 * 60 * 60 * 1000 // 7 days if "Remember Me" is ON
        : Date.now() + 24 * 60 * 60 * 1000; // 1 day otherwise
  
      localStorage.setItem("authToken", token);
      localStorage.setItem("authUser", JSON.stringify(user));
      localStorage.setItem("authExpiration", expirationTime);
  
      user.role === "admin" ? navigate("/admin") : navigate("/");
    } catch (err) {
      const errorMessage = err.response?.data?.msg || "Login failed";
      console.error("Login error:", err.response || err);
      alert(errorMessage);
    } finally {
      setLoading(false); // Stop loading after the try/catch
    }
  };
  
  const responseGoogle = async (authResult) => {
    setLoading(true);
    try {
      if (authResult['code']) {
        const response = await axios.get(
          `${API_URL}/api/auth/google?code=${authResult.code}`
        );
  
        const { token, user } = response.data;
  
        if (!user) throw new Error("User not found in response");
        if (user.blocked) {
          alert("User is blocked. Please contact support.");
          return;
        }
  
        dispatch(login({ token, user, role: user.role }));
  
        const expirationTime = Date.now() + 7 * 24 * 60 * 60 * 1000; // Token expiration logic for Google login
  
        localStorage.setItem("authToken", token);
        localStorage.setItem("authUser", JSON.stringify(user));
        localStorage.setItem("authExpiration", expirationTime);
  
        user.role === "admin" ? navigate("/admin") : navigate("/");
      }
    } catch (err) {
      console.error("Error during Google login:", err);
      alert("Failed to log in with Google. Please try again.");
    } finally {
      setLoading(false); // Stop loading after the try/catch
    }
  };
  
  const googleLogin = useGoogleLogin({
    onSuccess: responseGoogle,
    onError: (error) => {
      console.error("Google login error:", error);
      alert("Failed to log in with Google.");
    },
    flow: "auth-code", // Ensures you're using the OAuth2 authorization code flow
  });  

  const handleForgotPassword = async () => {
    setLoading(true);
    try {
      if (!email) {
        alert("Please enter your email to reset the password.");
        return;
      }
  
      const response = await axios.post(`${API_URL}/api/auth/reset-password`, { email });
      alert(response.data.message || "A reset link has been sent to your email.");
    } catch (error) {
      console.error("Forgot password error:", error);
      alert("Failed to send reset password link. Please try again.");
    } finally {
      setLoading(false); // Stop loading after the try/catch
    }
  };  

  return (
    <div className="h-screen flex flex-col md:flex-row font-clash">
      {/* Left Section: Image - Visible only on screens larger than 1024px */}
      <div className="hidden lg:block w-2/3">
        <img
          src={carleft}
          alt="image of a car"
          className="h-full w-full object-cover"
        />
      </div>

      {/* Right Section: Form */}
      <div className="w-full lg:w-1/3 h-screen bg-[#1f1f1f] flex justify-center items-center p-4 md:p-8">
        <form onSubmit={handleSubmit} className="w-full max-w-md bg-[#1f1f1f] p-8 rounded-md">
          <div>
            <img src={logo} alt="image of carvet pro logo" className="mb-4 mx-auto" />
          </div>
          <p className="text-left text-[18px] text-white font-semibold mb-4 mt-8">
            Nice to see you again
          </p>

          {/* Email Input */}
          <div className="mb-4">
            <label className="block mb-2 text-[#F0FFC5] text-[11px]" htmlFor="email">
              Login
            </label>
            <input
              type="email"
              id="email"
              className="border rounded w-full py-2 px-3 placeholder:text-[#808080] placeholder:text-[15px]"
              placeholder="Email or phone number"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          {/* Password Input with Eye Icon */}
          <div className="mb-4 relative">
            <label className="block mb-2 text-[#F0FFC5] text-[11px]" htmlFor="password">
              Password
            </label>
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              className="border rounded w-full py-2 px-3 placeholder:text-[#808080] placeholder:text-[15px]"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <div
              className="absolute top-9 right-3 cursor-pointer"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <AiOutlineEyeInvisible size={24} />
              ) : (
                <AiOutlineEye size={24} />
              )}
            </div>
          </div>

          {/* Forgot Password Link */}
          <div className="mb-8 flex items-center justify-end">
            <button
              className="text-blue-400 text-[13px] hover:underline"
              onClick={handleForgotPassword}
              disabled={loading}
            >
              Forgot Password?
            </button>
          </div>

          {loading ? (
            <div>
              <Loading />
            </div>
        ) : (
          <button
            type="submit"
            className="bg-[#007AFF] text-[#fff] text-[15px] flex items-center justify-center text-center py-2 px-4 rounded w-full"
            disabled={loading}
          >
              Sign in
          </button>
        )}

          <hr className="w-full mt-12" />
          <button
            type="button" // Changed from 'submit' to 'button' for Google login
            className="bg-[#333333] text-[#fff] text-[15px] py-3 mt-6 px-4 rounded w-full flex items-center justify-center space-x-2"
            onClick={googleLogin}
            disabled={loading}
          >
            <img src={google} alt="Google Logo" className="w-5 h-5" />
            <span>Or sign in with Google</span>
          </button>

          <div className="flex items-center justify-center gap-2 mt-4 text-[#F0FFC5] text-[12px]">
            <p className="">Don't have an account?</p>
            <a href="/signup" className="text-[#007AFF] text-[12px]">Sign Up</a>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
